import React, { useState, useEffect } from "react";
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (value) => {
  const number = typeof value === "number" ? value : parseFloat(String(value));
  if (isNaN(number)) return "0.00";
  return formatter.format(number);
};

const cellBorder = "1px solid var(--sl-border-secondary, #e9eaeb)";

const tdStyle = {
  padding: "8px 12px",
  verticalAlign: "top",
  borderBottom: cellBorder,
};

const integerMask = createNumberMask({
  prefix: "",
  decimalLimit: 0,
  includeThousandsSeparator: false,
});

const currencyMask = createNumberMask({
  prefix: "",
  allowDecimal: true,
  decimalLimit: 2,
  includeThousandsSeparator: false,
});

const typePillStyle = {
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  backgroundColor: "var(--sl-bg-active, #f4f9fc)",
  border: "1px solid var(--sl-border-secondary, #e5e7eb)",
  borderRadius: 999,
  padding: "4px 28px 4px 12px",
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='%23717680'><path d='M1 1l5 6 5-6'/></svg>")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center",
  backgroundSize: "10px 6px",
  cursor: "pointer",
  width: "auto",
  height: 32,
  display: "inline-block",
};

const inputBase = {
  border: "none",
  background: "transparent",
  padding: "4px 8px",
  width: "100%",
};

const rightCellStyle = {
  padding: "8px 12px",
  verticalAlign: "bottom",
  textAlign: "right",
  borderBottom: cellBorder,
};

const rightCellInner = {
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const rightInputStyle = {
  border: "none",
  background: "transparent",
  padding: "0 8px",
  height: 32,
  width: "100%",
  textAlign: "right",
};

const dragHandleCellStyle = {
  padding: "8px 4px 8px 12px",
  verticalAlign: "top",
  width: 24,
  borderBottom: cellBorder,
};

const dragHandleStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 0",
  color: "var(--sl-text-disabled, #a4a7ae)",
  cursor: "grab",
  textDecoration: "none",
};

const dollarGlyphStyle = {
  marginRight: 4,
};

const RentalInvoiceItemRow = ({
  rentalInvoiceItem,
  provided,
}) => {
  const {
    updateNestedAttributes,
    deleteNested,
  } = useRentalInvoiceEditContext();

  const updateRentalInvoiceItemState = (attributes) => {
    updateNestedAttributes("rental_invoice_items_attributes", {
      id: rentalInvoiceItem.id,
      ...attributes,
    });
  };

  const deleteRentalInvoiceItem = () => {
    deleteNested("rental_invoice_items_attributes", rentalInvoiceItem.id);
  };

  const [title, setTitle] = useState(rentalInvoiceItem.title || "");
  const [description, setDescription] = useState(rentalInvoiceItem.description || "");
  const [itemFocused, setItemFocused] = useState(false);
  const [quantity, setQuantity] = useState(rentalInvoiceItem.quantity || 0);
  const [costType, setCostType] = useState(rentalInvoiceItem.cost_type || "fixed");
  const [cost, setCost] = useState(rentalInvoiceItem.cost || 0);

  useEffect(() => {
    if (title !== (rentalInvoiceItem.title || "")) {
      updateRentalInvoiceItemState({ title });
    }
  }, [title]);

  useEffect(() => {
    if (description !== (rentalInvoiceItem.description || "")) {
      updateRentalInvoiceItemState({ description });
    }
  }, [description]);

  useEffect(() => {
    if (quantity !== rentalInvoiceItem.quantity) {
      updateRentalInvoiceItemState({ quantity });
    }
  }, [quantity]);

  useEffect(() => {
    if (costType !== rentalInvoiceItem.cost_type) {
      updateRentalInvoiceItemState({ cost_type: costType });
    }
  }, [costType]);

  useEffect(() => {
    if (cost !== rentalInvoiceItem.cost) {
      updateRentalInvoiceItemState({ cost });
    }
  }, [cost]);

  return (
    <tr
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={{ ...provided.draggableProps.style, background: "var(--sl-bg-primary, #fff)" }}
    >
      <td style={dragHandleCellStyle}>
        <div style={{ height: 32, display: "flex", alignItems: "center" }}>
          <a
            href="#"
            {...provided.dragHandleProps}
            onClick={(e) => e.preventDefault()}
            style={dragHandleStyle}
            aria-label="Drag to reorder"
          >
            <i className="far fa-grip-vertical" />
          </a>
        </div>
      </td>
      <td
        style={{ ...tdStyle, width: "auto", minWidth: 240 }}
        onFocus={() => setItemFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setItemFocused(false);
          }
        }}
      >
        <input
          type="text"
          className="form-control setlist-text-md setlist-font-semibold"
          style={{ ...inputBase, height: 32, paddingBottom: 0 }}
          placeholder="Item name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={(e) => e.target.select()}
        />
        {(itemFocused || description) && (
          <input
            type="text"
            className="form-control form-control-sm setlist-text-sm figma-text quaternary"
            style={{ ...inputBase, height: 32, paddingTop: 0 }}
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </td>

      <td style={{ ...rightCellStyle, width: 80 }}>
        <div style={rightCellInner}>
          <MaskedInput
            style={rightInputStyle}
            mask={integerMask}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </div>
      </td>

      <td style={{ ...rightCellStyle, width: 120 }}>
        <div style={rightCellInner}>
          <select
            className="setlist-text-sm figma-text secondary"
            style={typePillStyle}
            value={costType}
            onChange={(e) => setCostType(e.target.value)}
          >
            <option value="fixed">Fixed</option>
            <option value="per_head">Per head</option>
          </select>
        </div>
      </td>

      <td style={{ ...rightCellStyle, width: 120 }}>
        <div style={rightCellInner}>
          <span className="figma-text quaternary" style={dollarGlyphStyle}>$</span>
          <MaskedInput
            style={{ ...rightInputStyle, width: "auto", padding: 0 }}
            size={Math.max(String(cost ?? "").length, 1)}
            mask={currencyMask}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </div>
      </td>

      <td style={{ ...rightCellStyle, width: 120, whiteSpace: "nowrap" }}>
        <div style={rightCellInner} className="setlist-text-md setlist-font-semibold">
          <span className="figma-text quaternary" style={dollarGlyphStyle}>$</span>
          {formatAmount(rentalInvoiceItem.total)}
        </div>
      </td>

      <td style={{ ...rightCellStyle, width: 40 }}>
        <div style={rightCellInner}>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); deleteRentalInvoiceItem(); }}
            className="setlist-text-md figma-text quinary"
            style={{ textDecoration: "none" }}
          >
            <i className="far fa-trash" />
          </a>
        </div>
      </td>
    </tr>
  );
};

export default RentalInvoiceItemRow;
